import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  X,
  CheckSquare,
  Square,
  ShieldCheck,
  Sparkles,
  Loader2,
  ShieldAlert,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { StudioRole, ModulePermissionGroup, PermissionItem } from "../../../../Data/role.dto";
import { getModuleViewPermission, getModuleOrderIndex } from "../../../../Data/permissionsCatalog";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import {
  createRoleAPI,
  updateRoleAPI,
  getAllRolesAPI,
} from "../../../../Redux/ApiCalls/Dashboard/RoleApi";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../atoms/Utlis/Toast";

interface AddEditRoleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  roleToEdit?: StudioRole | null;
  catalog: ModulePermissionGroup[];
}

interface ViewPermissionPrompt {
  group: ModulePermissionGroup;
  targetPerm: PermissionItem;
  viewPerm: PermissionItem;
}

interface DeselectViewPrompt {
  group: ModulePermissionGroup;
  viewPerm: PermissionItem;
  dependentKeys: string[];
}

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)";

export const AddEditRoleDrawer: React.FC<AddEditRoleDrawerProps> = ({
  isOpen,
  onClose,
  roleToEdit,
  catalog,
}) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.role);

  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState(false);

  // Modal prompts for enforcing view permission requirement
  const [viewPrompt, setViewPrompt] = useState<ViewPermissionPrompt | null>(null);
  const [deselectPrompt, setDeselectPrompt] = useState<DeselectViewPrompt | null>(null);

  const isEditing = Boolean(roleToEdit && roleToEdit.id);

  // Total available permissions across all catalog modules
  const allCatalogKeys = React.useMemo(() => {
    const keys: string[] = [];
    catalog.forEach((group) => {
      group.permissions.forEach((p) => keys.push(p.key));
    });
    return keys;
  }, [catalog]);

  const sortedCatalog = React.useMemo(() => {
    return [...catalog].sort(
      (a, b) => getModuleOrderIndex(a.module) - getModuleOrderIndex(b.module)
    );
  }, [catalog]);

  useEffect(() => {
    if (isOpen) {
      if (roleToEdit) {
        setRoleName(roleToEdit.name || "");
        setDescription(roleToEdit.description || "");
        const permsSet = new Set(roleToEdit.permissions || []);
        // Auto-heal any legacy orphan permissions by adding the required view permission
        catalog.forEach((group) => {
          const viewPerm = getModuleViewPermission(group);
          if (viewPerm && !permsSet.has(viewPerm.key)) {
            const hasOrphans = group.permissions.some(
              (p) => p.key !== viewPerm.key && permsSet.has(p.key)
            );
            if (hasOrphans) {
              permsSet.add(viewPerm.key);
            }
          }
        });
        setSelectedPermissions(permsSet);
      } else {
        setRoleName("");
        setDescription("");
        setSelectedPermissions(new Set());
      }
      setTouched(false);
      setViewPrompt(null);
      setDeselectPrompt(null);
    }
  }, [isOpen, roleToEdit, catalog]);

  if (!isOpen) return null;

  const togglePermission = (key: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  /**
   * Main permission toggle handler enforcing:
   * 1. Action permissions cannot be selected without the module's view permission.
   * 2. Deselecting the view permission warns the user and cleans up dependent permissions.
   */
  const handlePermissionClick = (group: ModulePermissionGroup, perm: PermissionItem) => {
    const viewPerm = getModuleViewPermission(group);
    const isView = Boolean(viewPerm && perm.key === viewPerm.key);
    const isChecked = selectedPermissions.has(perm.key);

    if (isChecked) {
      // User is attempting to DESELECT this permission
      if (isView && viewPerm) {
        // Check if any other permissions in this group are currently selected
        const dependentKeys = group.permissions
          .filter((p) => p.key !== viewPerm.key && selectedPermissions.has(p.key))
          .map((p) => p.key);

        if (dependentKeys.length > 0) {
          // Show confirmation prompt to prevent orphan permissions
          setDeselectPrompt({
            group,
            viewPerm,
            dependentKeys,
          });
          return;
        }
      }

      // Normal deselect
      togglePermission(perm.key);
    } else {
      // User is attempting to SELECT this permission
      // If this is an action permission and view permission is not selected:
      if (!isView && viewPerm && !selectedPermissions.has(viewPerm.key)) {
        // Block selection! Show the required popup:
        // "For this permission view permission is required"
        setViewPrompt({
          group,
          targetPerm: perm,
          viewPerm,
        });
        return;
      }

      // Normal select
      togglePermission(perm.key);
    }
  };

  const handleAssignViewPermission = () => {
    if (!viewPrompt) return;
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      next.add(viewPrompt.viewPerm.key);
      next.add(viewPrompt.targetPerm.key);
      return next;
    });
    setViewPrompt(null);
  };

  const handleConfirmDeselectView = () => {
    if (!deselectPrompt) return;
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      next.delete(deselectPrompt.viewPerm.key);
      deselectPrompt.dependentKeys.forEach((k) => next.delete(k));
      return next;
    });
    setDeselectPrompt(null);
  };

  const isModuleFullySelected = (group: ModulePermissionGroup): boolean => {
    if (!group.permissions.length) return false;
    return group.permissions.every((p) => selectedPermissions.has(p.key));
  };

  const isModulePartiallySelected = (group: ModulePermissionGroup): boolean => {
    const count = group.permissions.filter((p) => selectedPermissions.has(p.key)).length;
    return count > 0 && count < group.permissions.length;
  };

  const toggleModuleAll = (group: ModulePermissionGroup) => {
    const allSelected = isModuleFullySelected(group);
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      group.permissions.forEach((p) => {
        if (allSelected) {
          next.delete(p.key);
        } else {
          next.add(p.key);
        }
      });
      return next;
    });
  };

  const selectAllPermissions = () => {
    setSelectedPermissions(new Set(allCatalogKeys));
  };

  const clearAllPermissions = () => {
    setSelectedPermissions(new Set());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);

    if (!roleName.trim()) {
      showErrorToast("Role name is required");
      return;
    }

    if (selectedPermissions.size === 0) {
      showErrorToast("Please select at least one permission for this role");
      return;
    }

    // Enforce invariant: Action permissions require their module's view permission
    for (const group of catalog) {
      const viewPerm = getModuleViewPermission(group);
      if (viewPerm && !selectedPermissions.has(viewPerm.key)) {
        const orphanPerm = group.permissions.find(
          (p) => p.key !== viewPerm.key && selectedPermissions.has(p.key)
        );
        if (orphanPerm) {
          showErrorToast(`For this permission (${orphanPerm.label}), view permission is required`);
          return;
        }
      }
    }

    const payload = {
      name: roleName.trim(),
      description: description.trim() || undefined,
      permissions: Array.from(selectedPermissions),
    };

    try {
      if (isEditing && roleToEdit) {
        await dispatch(updateRoleAPI({ id: roleToEdit.id, data: payload })).unwrap();
        showSuccessToast("Role updated successfully!");
      } else {
        await dispatch(createRoleAPI(payload)).unwrap();
        showSuccessToast("Role created successfully!");
      }
      dispatch(getAllRolesAPI());
      onClose();
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to save role");
    }
  };

  return (
    <>
      <Overlay onClick={onClose}>
        <DrawerContainer onClick={(e) => e.stopPropagation()}>
        {/* Header matching screenshot */}
        <DrawerHeader>
          <HeaderLeft>
            <CloseButton type="button" onClick={onClose} aria-label="Close drawer">
              <X size={20} />
            </CloseButton>
            <HeaderTitle>{isEditing ? "Edit Role" : "Add Role"}</HeaderTitle>
          </HeaderLeft>
          <GlobalToggles>
            <TextLink type="button" onClick={selectAllPermissions}>
              Select all
            </TextLink>
            <DividerDot>•</DividerDot>
            
          </GlobalToggles>
        </DrawerHeader>

        <Form onSubmit={handleSubmit}>
          <ScrollContent>
            {/* Role Name */}
            <FieldGroup>
              <Label>
                Role Name <Required>*</Required>
              </Label>
              <Input
                type="text"
                placeholder="e.g. Studio Manager, Lead Photographer, Retoucher"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                autoFocus
              />
              {touched && !roleName.trim() && (
                <ErrorText>Role name is required</ErrorText>
              )}
            </FieldGroup>

            {/* Description */}
            <FieldGroup>
              <Label>Description</Label>
              <Textarea
                rows={2}
                placeholder="Briefly describe what responsibilities and scope this role has..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FieldGroup>

            {/* Permissions Header & Counter */}
            <PermissionsBanner>
              <BannerLeft>
                <ShieldCheck size={16} color="#c94daa" />
                <BannerText>Module Permissions</BannerText>
              </BannerLeft>
              <BadgeCount>
                {selectedPermissions.size} / {allCatalogKeys.length} selected
              </BadgeCount>
            </PermissionsBanner>

            {/* Module Checklist (Matching screenshot: MODULE_NAME on left / top, checkboxes in row) */}
            <ModuleList>
              {sortedCatalog.map((group) => {
                const fullySelected = isModuleFullySelected(group);
                const partiallySelected = isModulePartiallySelected(group);

                return (
                  <ModuleRow key={group.module}>
                    <ModuleHeader>
                      <ModuleTitleWrap onClick={() => toggleModuleAll(group)}>
                        <ModuleTitle>{group.module}</ModuleTitle>
                        <ModuleSubtitle>{group.label}</ModuleSubtitle>
                      </ModuleTitleWrap>

                      <ModuleSelectAllBtn
                        type="button"
                        onClick={() => toggleModuleAll(group)}
                        $active={fullySelected || partiallySelected}
                      >
                        {fullySelected ? "Deselect all" : "Select all"}
                      </ModuleSelectAllBtn>
                    </ModuleHeader>

                    <PermissionsGrid>
                      {group.permissions.map((perm) => {
                        const isChecked = selectedPermissions.has(perm.key);
                        const viewPerm = getModuleViewPermission(group);
                        const isView = Boolean(viewPerm && perm.key === viewPerm.key);
                        const isLocked = !isView && Boolean(viewPerm && !selectedPermissions.has(viewPerm.key));
                        // Short action label (e.g. "read", "create", "update", "delete" or human label)
                        const shortLabel =
                          perm.label || perm.key.split(".").pop() || perm.key;

                        return (
                          <CheckboxItem
                            key={perm.key}
                            $checked={isChecked}
                            $isView={isView}
                            $isLocked={isLocked}
                            onClick={() => handlePermissionClick(group, perm)}
                            title={
                              isLocked
                                ? "For this permission view permission is required"
                                : perm.description || perm.label
                            }
                          >
                            <CheckboxIcon $checked={isChecked} $isLocked={isLocked}>
                              {isChecked ? (
                                <CheckSquare size={16} color="#c94daa" />
                              ) : (
                                <Square size={16} color={isLocked ? "#cbd5e1" : "#9ca3af"} />
                              )}
                            </CheckboxIcon>
                            <CheckboxLabel $isView={isView} $isLocked={isLocked}>
                              {shortLabel}
                              {isView && <ViewTag>View</ViewTag>}
                            </CheckboxLabel>
                          </CheckboxItem>
                        );
                      })}
                    </PermissionsGrid>
                  </ModuleRow>
                );
              })}
            </ModuleList>
          </ScrollContent>

          {/* Footer Action Bar */}
          <DrawerFooter>
            <CounterInfo>
              <strong>{selectedPermissions.size}</strong> of {allCatalogKeys.length} permissions enabled
            </CounterInfo>
            <FooterActions>
              <CancelButton type="button" onClick={onClose} disabled={loading.save}>
                Cancel
              </CancelButton>
              <SaveButton type="submit" disabled={loading.save}>
                {loading.save ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : isEditing ? (
                  "Save Changes"
                ) : (
                  "Create Role"
                )}
              </SaveButton>
            </FooterActions>
          </DrawerFooter>
        </Form>
      </DrawerContainer>
    </Overlay>

    {/* View Permission Required Popup Modal */}
    {viewPrompt && (
      <ModalBackdrop onClick={() => setViewPrompt(null)}>
        <ModalCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <ModalCardHeader>
            <ModalHeaderLeft>
              <ModalIconBadge>
                <ShieldAlert size={20} color="#c94daa" />
              </ModalIconBadge>
              <div>
                <ModalCardTitle>View Permission Required</ModalCardTitle>
                <ModalCardCategory>{viewPrompt.group.label}</ModalCardCategory>
              </div>
            </ModalHeaderLeft>
            <ModalCloseBtn
              type="button"
              onClick={() => setViewPrompt(null)}
              aria-label="Close dialog"
            >
              <X size={18} />
            </ModalCloseBtn>
          </ModalCardHeader>

          <ModalCardBody>
            <NoticeAlertBox>
              <AlertNoticeTitle>
                For this permission view permission is required
              </AlertNoticeTitle>
              <AlertNoticeDesc>
                To enable <strong>"{viewPrompt.targetPerm.label}"</strong>, the base{" "}
                <strong>"{viewPrompt.viewPerm.label}"</strong> permission must be assigned first so this feature can appear in the navigation and be accessed.
              </AlertNoticeDesc>
            </NoticeAlertBox>

            <PermissionFlowBox>
              <FlowStep $active>
                <FlowStepDot />
                <FlowStepText>
                  <span>Prerequisite</span>
                  <strong>{viewPrompt.viewPerm.label}</strong>
                </FlowStepText>
              </FlowStep>
              <FlowArrow>
                <ArrowRight size={16} />
              </FlowArrow>
              <FlowStep>
                <FlowStepDot $target />
                <FlowStepText>
                  <span>Requested</span>
                  <strong>{viewPrompt.targetPerm.label}</strong>
                </FlowStepText>
              </FlowStep>
            </PermissionFlowBox>
          </ModalCardBody>

          <ModalCardFooter>
            <ModalCancelBtn type="button" onClick={() => setViewPrompt(null)}>
              Cancel
            </ModalCancelBtn>
            <AssignViewBtn type="button" onClick={handleAssignViewPermission}>
              Assign view permission
            </AssignViewBtn>
          </ModalCardFooter>
        </ModalCard>
      </ModalBackdrop>
    )}

    {/* Deselect View Confirmation Modal */}
    {deselectPrompt && (
      <ModalBackdrop onClick={() => setDeselectPrompt(null)}>
        <ModalCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <ModalCardHeader>
            <ModalHeaderLeft>
              <ModalIconBadge $variant="danger">
                <AlertTriangle size={20} color="#ef4444" />
              </ModalIconBadge>
              <div>
                <ModalCardTitle>Remove Dependent Permissions?</ModalCardTitle>
                <ModalCardCategory>{deselectPrompt.group.label}</ModalCardCategory>
              </div>
            </ModalHeaderLeft>
            <ModalCloseBtn
              type="button"
              onClick={() => setDeselectPrompt(null)}
              aria-label="Close dialog"
            >
              <X size={18} />
            </ModalCloseBtn>
          </ModalCardHeader>

          <ModalCardBody>
            <NoticeAlertBox $isDanger>
              <AlertNoticeTitle>
                View permission is required for all other actions
              </AlertNoticeTitle>
              <AlertNoticeDesc>
                Deselecting <strong>"{deselectPrompt.viewPerm.label}"</strong> will also remove the following active permissions in <strong>{deselectPrompt.group.label}</strong>:
              </AlertNoticeDesc>
            </NoticeAlertBox>

            <DependentPillsList>
              {deselectPrompt.dependentKeys.map((key) => {
                const perm = deselectPrompt.group.permissions.find((p) => p.key === key);
                return (
                  <DependentPill key={key}>
                    <X size={12} color="#ef4444" />
                    <span>{perm?.label || key}</span>
                  </DependentPill>
                );
              })}
            </DependentPillsList>
          </ModalCardBody>

          <ModalCardFooter>
            <ModalCancelBtn type="button" onClick={() => setDeselectPrompt(null)}>
              Keep view permission
            </ModalCancelBtn>
            <DangerConfirmBtn type="button" onClick={handleConfirmDeselectView}>
              Remove all permissions
            </DangerConfirmBtn>
          </ModalCardFooter>
        </ModalCard>
      </ModalBackdrop>
    )}
  </>
);
};

export default AddEditRoleDrawer;

/* Styled Components */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const DrawerContainer = styled.div`
  width: 100%;
  max-width: 680px;
  height: 100vh;
  background: #ffffff;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #ffffff;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #737373;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    color: #171717;
    background: #f5f5f5;
  }
`;

const HeaderTitle = styled.h2`
  font-size: 19px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`;

const GlobalToggles = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TextLink = styled.button`
  background: transparent;
  border: none;
  color: #c94daa;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const DividerDot = styled.span`
  color: #d4d4d4;
  font-size: 12px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
`;

const Required = styled.span`
  color: #ef4444;
`;

const Input = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  padding: 0 14px;
  font-size: 14px;
  color: #171717;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  padding: 10px 14px;
  font-size: 14px;
  color: #171717;
  outline: none;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #ef4444;
`;

const PermissionsBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #faf5ff;
  border: 1px solid rgba(201, 77, 170, 0.15);
  border-radius: 12px;
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BannerText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #171717;
`;

const BadgeCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #c94daa;
  background: #ffffff;
  padding: 4px 10px;
  border-radius: 9999px;
  border: 1px solid rgba(201, 77, 170, 0.2);
`;

const ModuleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModuleRow = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  padding: 14px 16px;
  background: #ffffff;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #e5e5e5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  }
`;

const ModuleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #f0f0f0;
`;

const ModuleTitleWrap = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  cursor: pointer;
`;

const ModuleTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #171717;
  text-transform: uppercase;
`;

const ModuleSubtitle = styled.span`
  font-size: 12px;
  color: #737373;
`;

const ModuleSelectAllBtn = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: none;
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => (props.$active ? "#c94daa" : "#8c8c8c")};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover {
    background: #fdf2f8;
    color: #c94daa;
  }
`;

const PermissionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
`;

const CheckboxItem = styled.div<{ $checked: boolean; $isView?: boolean; $isLocked?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  user-select: none;
  padding: 5px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
  background: ${(props) => (props.$checked ? "rgba(201, 77, 170, 0.05)" : "transparent")};
  opacity: ${(props) => (props.$isLocked ? 0.75 : 1)};

  &:hover {
    background: ${(props) =>
      props.$checked
        ? "rgba(201, 77, 170, 0.1)"
        : props.$isLocked
        ? "#fef2f2"
        : "#f5f5f5"};
  }
`;

const CheckboxIcon = styled.div<{ $checked: boolean; $isLocked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckboxLabel = styled.span<{ $isView?: boolean; $isLocked?: boolean }>`
  font-size: 13px;
  color: ${(props) => (props.$isView ? "#171717" : props.$isLocked ? "#6b7280" : "#404040")};
  font-weight: ${(props) => (props.$isView ? "600" : "400")};
  display: inline-flex;
  align-items: center;
`;

const ViewTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #fdf2f8;
  color: #c94daa;
  border: 1px solid rgba(201, 77, 170, 0.3);
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: 6px;
`;

/* View Permission Prompt & Confirmation Modal Styles */

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  z-index: 10100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalCard = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 490px;
  border-radius: 16px;
  box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: popIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.96) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalIconBadge = styled.div<{ $variant?: "warning" | "danger" }>`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${(props) => (props.$variant === "danger" ? "#fef2f2" : "#fdf2f8")};
  border: 1px solid
    ${(props) => (props.$variant === "danger" ? "#fecaca" : "rgba(201, 77, 170, 0.25)")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ModalCardTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const ModalCardCategory = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #c94daa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModalCloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    color: #0f172a;
    background: #f1f5f9;
  }
`;

const ModalCardBody = styled.div`
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const NoticeAlertBox = styled.div<{ $isDanger?: boolean }>`
  background: ${(props) => (props.$isDanger ? "#fef2f2" : "#fdf2f8")};
  border: 1px solid ${(props) => (props.$isDanger ? "#fecaca" : "rgba(201, 77, 170, 0.25)")};
  border-radius: 12px;
  padding: 14px 16px;
`;

const AlertNoticeTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  margin: 0 0 6px 0;
`;

const AlertNoticeDesc = styled.p`
  font-size: 13px;
  color: #4b5563;
  line-height: 1.5;
  margin: 0;

  strong {
    color: #111827;
  }
`;

const PermissionFlowBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 12px 16px;
`;

const FlowStep = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const FlowStepDot = styled.div<{ $target?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => (props.$target ? "#64748b" : "#c94daa")};
  box-shadow: 0 0 0 3px
    ${(props) => (props.$target ? "rgba(100, 116, 139, 0.15)" : "rgba(201, 77, 170, 0.2)")};
  flex-shrink: 0;
`;

const FlowStepText = styled.div`
  display: flex;
  flex-direction: column;

  span {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  strong {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
  }
`;

const FlowArrow = styled.div`
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DependentPillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DependentPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
`;

const ModalCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 22px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
`;

const ModalCancelBtn = styled.button`
  padding: 9px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const AssignViewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(201, 77, 170, 0.25);
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(201, 77, 170, 0.35);
  }
`;

const DangerConfirmBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 10px;
  border: none;
  background: #ef4444;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(239, 68, 68, 0.35);
  }
`;


const DrawerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const CounterInfo = styled.div`
  font-size: 13px;
  color: #525252;

  strong {
    color: #171717;
  }
`;

const FooterActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelButton = styled.button`
  padding: 9px 18px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  color: #525252;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    color: #171717;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(206, 86, 176, 0.25);
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(206, 86, 176, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
